import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { SectionTab } from '../Components/SectionTabs';

export interface UseTabStateOptions {
	tabs: SectionTab[];
	/** The tab the URL's :tab segment asked for (may belong to the page's other tab group). */
	initialTab: string;
	fallbackId: string;
	mode: EntityFormMode;
	/** URL for a tab in this group. Selecting a tab points the shared :tab segment at it. */
	urlFor: (tabId: string) => string;
	/** While editing, only these tab ids accept a switch; omit for an unlocked group. */
	editSessionTabs?: string[];
	/**
	 * How to treat an initialTab this group doesn't own: 'fallback' resets to fallbackId
	 * (single-group page), 'ignore' keeps the current selection (the tab likely belongs to the
	 * page's other group, which will pick it up).
	 */
	unknownInitialTab: 'fallback' | 'ignore';
	/**
	 * Whether selecting a tab mirrors into the URL's :tab segment (default true). Set false for a
	 * group that shouldn't drive navigation — e.g. a bottom-of-page group where a pathname change
	 * would trigger a scroll-to-top. The initial selection still honours initialTab.
	 */
	syncUrl?: boolean;
}

/**
 * One tab group on an account page: resolves unknown ids to the fallback, refuses to leave the
 * edit session's tabs while editing, and mirrors the selection into the URL (replace, and only
 * outside edit mode — an edit session shouldn't rewrite history or the address bar).
 */
export function useTabState({
	tabs,
	initialTab,
	fallbackId,
	mode,
	urlFor,
	editSessionTabs,
	unknownInitialTab,
	syncUrl = true,
}: UseTabStateOptions): [string, (tabId: string) => void] {
	const navigate = useNavigate();

	const resolve = (name?: string) => (name && tabs.some(t => t.id === name) ? name : fallbackId);

	const [activeTab, setActiveTab] = useState(() => resolve(initialTab));

	const setTab = (tabId: string) => {
		const resolved = resolve(tabId);
		if (mode === EntityFormMode.EDIT && editSessionTabs && !editSessionTabs.includes(resolved)) {
			return;
		}
		if (syncUrl && mode !== EntityFormMode.EDIT) {
			const newPath = urlFor(resolved);
			if (window.location.pathname !== newPath) {
				navigate(newPath, { replace: true });
			}
		}
		setActiveTab(resolved);
	};

	useEffect(() => {
		if (tabs.some(t => t.id === initialTab)) {
			setActiveTab(initialTab);
		} else if (unknownInitialTab === 'fallback') {
			setActiveTab(fallbackId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialTab]);

	return [activeTab, setTab];
}
